import { isNumber } from 'constants/regexExpressions';
import MarketingStatus from 'enums/marketingStatus';
import createPictureImageSources from 'utils/createPictureImageSources';
import { imagesComingSoonIconSvg } from 'constants/svg-assets';
import CommunityType from 'enums/communityType';

/**
 * Represents a Master Planned Paid Summary Card component.
 */
export default class PaidCommunitySummaryCard {
  /**
   * Sets the information for the summary card.
   *
   * @param {Object} summaryCard - The summary card object.
   */
  setInformation(summaryCard) {
    this.summaryCard = summaryCard;
    this.id = 'summaryCard' + this.summaryCard.Id;
    this.isSpotlight = this.summaryCard?.IsFeaturedListing;
    this.errorImage = imagesComingSoonIconSvg;
    const { sources, src } = createPictureImageSources({
      url: this.summaryCard.Thumbnail || this.errorImage,
      breakpoints: {
        '2x': {
          width: 286,
          height: 208
        },
        '3x': {
          width: 350,
          height: 312
        }
      },
      irsParameters: {
        mode: 'crop',
        progressive: true
      }
    });
    this.sources = sources;
    this.src = src;
  }

  /**
   * Emits a custom event when a link is clicked on the summary card.
   */
  emitLinkClickEvent(event) {
    if (event.target?.closest('[data-summary-card-cta]')) {
      return;
    }

    const linkClickEvent = new CustomEvent('summaryCardLinkClick', {
      detail: {
        summaryCard: this.summaryCard
      }
    });

    document.dispatchEvent(linkClickEvent);
  }

  /**
   * Sets the label for the PaidSummaryCard.
   * @param {string} label - The label to set.
   */
  setLabel(label) {
    this.label = label;
  }

  /**
   * Handles the error event for the image element in the summary card.
   * If the image fails to load, it sets the error image as the source and removes all source elements from the parent
   * @param {Event} event - The error event object.
   */
  handleOnErrorImage(event) {
    const picture = event.target.parentElement;
    event.target.src = this.errorImage;

    picture?.querySelectorAll('source').forEach(source => source.remove());
    event.target.onerror = null;
  }

  /**
   * Binds events to the summary card.
   */
  bindEvents() {
    const element = this.getElement();
    element.addEventListener('click', this.emitLinkClickEvent.bind(this));

    const callToActionButton = element.querySelector('[data-summary-card-cta]');
    callToActionButton?.addEventListener('click', this.emitCtaClickEvent.bind(this));

    const image = element.querySelector('img');
    image?.addEventListener('error', this.handleOnErrorImage.bind(this));
  }

  /**
   * Returns the call to action based on the summary card properties.
   * @returns {string} The call to action text.
   */
  getCallToAction() {
    const { CommunityProjectType, IsLimited } = this.summaryCard;
    if (IsLimited) {
      return '';
    }
  
    if (this.summaryCard.MarketingStatus.Key === MarketingStatus.ComingSoon.toString()) {
      return 'Get more information';
    }
  
    if (
      !IsLimited &&
      CommunityProjectType.Key !== CommunityType.CustomLocation.toString() &&
      CommunityProjectType.Key !== CommunityType.Custom.toString()
    ) {
      return 'Free brochure';
    }
  
    return '';
  }

  /**
   * Emits a custom event when the CTA (Call to Action) is clicked.
   */
  emitCtaClickEvent() {
    const callToAction = this.getCallToAction();
    const element = this.getElement();
    const target = element.dataset.clickTarget;

    const callToActionClickEvent = new CustomEvent('summaryCardCtaClick', {
      detail: {
        name: 'leadGenerator',
        type: this.summaryCard.Type,
        id: this.summaryCard.Id,
        url: this.summaryCard.Url,
        marker: this.summaryCard,
        variation: 'paid',
        callToAction,
        target,
      },
    });

    document.dispatchEvent(callToActionClickEvent);
  }

  /**
   * Gets the HTML element with the specified ID.
   * @returns {HTMLElement} The HTML anchor element.
   */
  getElement() {
    return document.getElementById(this.id);
  }

  /**
   * Get the price of the summary card.
   * @returns {string} The price of the summary card.
   */
  getPrice() {
    const price = this.summaryCard.Price;
    const containsNumbers = isNumber.test(price);

    if (containsNumbers) {
      return `From ${price}`;
    }

    return price;
  }

  /**
   * Get the title of the summary card.
   * @returns {string} The title of the summary card.
   */
  getPriceTitle() {
    return `<h4 class="nhs-c-card--summary__price" data-qa="map_card_price">
                <span class='nhs-u-text--ellipsis'>${this.getPrice()}</span>
            </h4>`;
  }

  /**
   * Retrieves the image element for the summary card.
   *
   * @returns {string} The HTML string representing the image element.
   */
  getImage() {
    return `
            <picture class="nhs-c-card--summary__image" data-marketId="${
              this.summaryCard.Id
            }">
                ${this.sources
                  .map(
                    source =>
                      `<source srcset="${source.srcset}" type="${source.format}"></source>`
                  )
                  .join('')}
                <img 
                    src="${this.src}"
                    data-qa="map-image"
                    data-summary-img
                    class="nhs-c-card--summary__image"
                    data-src="${this.errorImage}"
                >
            </picture>
        `;
  }

  /**
   * Returns a string summarizing the number of communities and builders for the master planned community card.
   *
   * - Retrieves the CommunityCount and BuilderCount from the summaryCard object.
   * - Formats the string as "{CommunityCount} Communities | {BuilderCount} Builders".
   * - If either value is missing or falsy, it will display as "undefined" or "0" in the output.
   *
   * @returns {string} A summary string of the number of communities and builders.
  */
  getSpecifications() {
    const labelParts = [];
    if (this.summaryCard?.CommunityCount > 0) {
      const communitiesLabel = this.summaryCard.CommunityCount > 1 ? 'Communities' : 'Community';
      labelParts.push(`<div><b>${this.summaryCard.CommunityCount}</b> ${communitiesLabel}</div>`);
    }

    if (this.summaryCard?.BuilderCount > 0) {
      const buildersLabel = this.summaryCard.BuilderCount > 1 ? 'Builders' : 'Builder';
      labelParts.push(`<div><b>${this.summaryCard.BuilderCount}</b> ${buildersLabel}</div>`);
    }

    const html = labelParts.join(' <span>|</span> ');

    return `
      <div class='nhs-c-card--summary__specifications nhs-u-text--ellipsis' data-qa="map_card_specifications">
        ${html}
      </div>
    `;
  }

  /**
  * Returns the lead generator opener based on the summary card information.
  * @returns {string} The lead generator opener.
  */
  getLeadGeneratorOpener() {
    if (this.summaryCard.MarketingStatus.Key === MarketingStatus.ComingSoon.toString()) {
      return 'more-information-paid';
    }

    return 'get-brochure-paid';
  }

  /**
   * Returns the HTML string for the call to action required for the mini cards.
   *
   * - Checks if the CTA string is available.
   *
   * @returns {string} The HTML string for the call to action button, or an empty string if not applicable.
   */
  getCallToActionButton() {
    if (this.isSitePlan) {
      return '';
    }

    const callToAction = this.getCallToAction();

    if (!callToAction) {
      return '';
    }

    const opener = this.getLeadGeneratorOpener();

    return `<button
      class="nhs-c-button--text-only nhs-c-button--primary-lead nhs-c-button--small-32"
      data-summary-card-cta 
      data-qa="map-cta" 
      data-lead-generator 
      data-lead-generator-opener="${opener}"
      data-button-action="${callToAction}" 
      data-from-page="CommunityResults.MapCard.Community"
      data-community-id="${this.summaryCard.Id}">
      ${callToAction}
    </button>`;
  }

  /**
   * Returns the HTML representation of the summary card.
   * @returns {string} The HTML string representing the summary card.
   */
  getHtml() {
      const classNames = ['nhs-c-card--summary', 'nhs-mq-card--summary-medium'];
      if (this.isSpotlight) {
        classNames.push('nhs-c-card--summary-spotlight');
      }
  
      return `
              <div id="summaryCard${this.summaryCard.Id}" data-qa="map-card"
                  data-summary-content
                  data-latitude="${this.summaryCard.Latitude}"
                  data-longitude="${this.summaryCard.Longitude}"
                  data-community-type="${
                    this.summaryCard.CommunityProjectType.Key
                  }"                  
                  ${this.isSpotlight ? `data-is-featured-listing = true` : ''}
                  class="${classNames.join(' ')}"
                  data-card
                  data-id="${this.summaryCard.Id}"
                  data-community-id="${this.summaryCard.Id}"
                  data-is-coming-soon="${
                    this.summaryCard.MarketingStatus.Key ===
                    MarketingStatus.ComingSoon.toString()
                  }"
                  data-market-id="${this.summaryCard.MarketId}"
                  data-brand-name="${this.summaryCard.BrandName}"
                  data-city="${this.summaryCard.City}"
                  data-type="${this.summaryCard.Type}"
                  data-zip="${this.summaryCard.Zip}"
                  data-url="${this.summaryCard.Url}"
                  data-has-builder-reviews="${+this.summaryCard.BuilderRating > 0}"
                  data-price-low="${this.summaryCard.PriceLow}"
                  data-price-high="${this.summaryCard.PriceHigh}"
                  data-community-name="${this.summaryCard.Name}"
                  data-marketing-status="${this.summaryCard.MarketingStatus.Name}"
                  data-price-reduction-label="${
                    this.summaryCard.PriceReductionLabel
                  }"
                  data-has-limited-information="${this.summaryCard.IsLimited}"
                  data-is-basic="false"
                  data-click-target="SRP Map Card"
                  data-zip-code="${this.summaryCard.Zip}"
                  data-builder-id="${this.summaryCard.BuilderId}"
                  data-market-name="${this.summaryCard.MarketName}"
                  data-state-abbreviation="${this.summaryCard.StateAbbreviation}"
                  data-state-name="${this.summaryCard.StateName}"
              >      
                <a 
                  href="${this.summaryCard.Url}" 
                  target="_blank" 
                  class="nhs-c-card--summary__wrapper">         
                    ${this.getImage()}                
                    <div                    
                      data-summary-card-link 
                      class="nhs-c-card--summary__body">
                      <h3 class="nhs-c-card--summary__title nhs-u-text--ellipsis" data-qa="Map-Title" >
                        ${this.summaryCard.Name}
                      </h3>
                      ${this.getPriceTitle()}                    
                      ${this.getSpecifications()}                    
                      <p class="nhs-c-card--summary__location nhs-u-text--ellipsis">
                        Master planned community
                      </p>        
                      <p class="nhs-c-card--summary__location nhs-u-text--ellipsis" data-qa="map-card-market">
                        ${this.summaryCard.City}, 
                        ${this.summaryCard.StateAbbreviation} 
                        ${this.summaryCard.Zip}
                      </p>
                    </div>
                </a>
                ${this.getCallToActionButton()}
              </div>
          `;
    }
}
